import { Box, Checkbox, FormControlLabel, IconButton, InputAdornment, Modal, Skeleton, TextField } from "@mui/material"
import { ContentCopy, Visibility, VisibilityOff } from "@mui/icons-material"
import { useEffect, useState } from "react"
import { Button } from "react-bootstrap";
import { Context, InstanceSystemSettings, LoggedInUser, TenantInformation } from "../../../types/types";
import { toast } from 'react-toastify';
import { get, post } from "../../../util/Axios";

export interface SystemSettingsPopupProps {
    selectedTenant: TenantInformation | undefined,
    open: boolean,
    close: Function,
    context: Context,
}


function SystemSettingsPopup(props: SystemSettingsPopupProps) {

    const [buttonsDisabled, setButtonsDisabled] = useState(false)
    const [systemSettings, setSystemSettings] = useState<InstanceSystemSettings | undefined>()
    const [showScimToken, setShowScimToken] = useState(false)

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '50%',
        minHeight: '20%',
        maxHeight: '75%',
        bgcolor: 'background.paper',
        border: '2px solid #000',
        boxShadow: 24,
        overflow: 'hidden',
        p: 4,
    };

    useEffect(() => {
        if (!props.open || !props.selectedTenant) {
            setSystemSettings(undefined)
            return;
        }

        setButtonsDisabled(true)
        setShowScimToken(false)
        get(`/contexts/${props.context.id}/tenants/${props.selectedTenant.id}/system-settings`).then(response => {
            const data: InstanceSystemSettings = response.data
            setSystemSettings(data)
            setButtonsDisabled(false)
        }).catch(error => {
            setButtonsDisabled(false)
            return toast.error(`Error fetching System Settings of tenant - ${error.response.status}: ${error.response.data}`)
        })
    }, [props.open])

    const copyScimToken = () => {
        if (!systemSettings) return;
        navigator.clipboard.writeText(systemSettings.scim_token)
        toast.success("SCIM Token copied to clipboard!")
    }

    const toggleLanguage = (key: string) => {
        setSystemSettings(old => old && {...old, languages: old.languages.map(language => language.key === key ? {...language, enabled: !language.enabled} : language)})
    }

    const onSave = () => {
        if (!props.selectedTenant || !systemSettings) return;

        setButtonsDisabled(true)
        post(`/contexts/${props.context.id}/tenants/${props.selectedTenant.id}/system-settings`, systemSettings).then(response => {
            setButtonsDisabled(false)
            if (response.status !== 200) {
                toast.error(`Error saving System Settings ${response.status}: ${response.data}`)
                return;
            }
            toast.success("System Settings saved successfully!")
            props.close()
        }).catch(error => {
            setButtonsDisabled(false)
            toast.error(`Error ${error.response.status}: ${error.response.data}`)
        })
    }

    return (
        <Modal
            open={props.open}
            onClose={() => {
                if (buttonsDisabled) return;
                    props.close()
                }}
        >
            <Box sx={style}>
                {systemSettings ? (
                <div style={{display: 'flex', flexDirection: 'column', gap: '15px'}}>
                    <span className="bold">System Settings</span>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={systemSettings.kiosk_mode_enabled}
                                onChange={(e) => setSystemSettings(old => old && {...old, kiosk_mode_enabled: e.target.checked})}
                            />
                        }
                        label="Kiosk Mode"
                    />
                    {systemSettings.kiosk_mode_enabled && <div>
                        <TextField
                            label="Kiosk Timer Seconds"
                            type="number"
                            value={systemSettings.kiosk_timer_in_seconds}
                            onChange={(e) => setSystemSettings(old => old && {...old, kiosk_timer_in_seconds: Number(e.target.value)})}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 260 }}
                        />
                    </div>}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={systemSettings.teamboard_templates_enabled}
                                onChange={(e) => setSystemSettings(old => old && {...old, teamboard_templates_enabled: e.target.checked})}
                            />
                        }
                        label="Teamboard Templates"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={systemSettings.scim_enabled}
                                onChange={(e) => setSystemSettings(old => old && {...old, scim_enabled: e.target.checked})}
                            />
                        }
                        label="SCIM"
                    />
                    {systemSettings.scim_enabled && <div>
                        <TextField
                            label="SCIM Token"
                            type={showScimToken ? "text" : "password"}
                            value={systemSettings.scim_token}
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowScimToken(old => !old)} edge="end">
                                            {showScimToken ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                        <IconButton onClick={copyScimToken} edge="end">
                                            <ContentCopy />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 320 }}
                        />
                    </div>}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={systemSettings.anonymization_enabled}
                                onChange={(e) => setSystemSettings(old => old && {...old, anonymization_enabled: e.target.checked})}
                            />
                        }
                        label="Anonymization Enabled"
                    />
                    <div>
                        <label style={{marginBottom: '4px', display: 'block'}}>Languages</label>
                        <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px 16px'}}>
                            {systemSettings.languages.map(language => (
                                <FormControlLabel
                                    key={language.key}
                                    control={
                                        <Checkbox
                                            checked={language.enabled}
                                            onChange={() => toggleLanguage(language.key)}
                                        />
                                    }
                                    label={language.name}
                                />
                            ))}
                        </div>
                    </div>
                    <div style={{display: 'flex', gap: '10px'}}>
                        <Button variant="secondary" disabled={buttonsDisabled} onClick={() => props.close()}>Cancel</Button>
                        <Button disabled={buttonsDisabled} onClick={onSave}>Save</Button>
                    </div>
                </div>
                ) : <div>
                        <Skeleton/>
                    </div>}
            </Box>
        </Modal>
    )
}

export default SystemSettingsPopup